// src/utils/imageUtils.js

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Converte uma string Data URL (base64) para um objeto File.
 * @param {string} dataUrl - A string no formato 'data:image/jpeg;base64,...'.
 * @param {string} filename - O nome do arquivo a ser criado (ex: 'encomenda.jpeg').
 * @returns {File} - O objeto File resultante.
 */
export function dataURLtoFile(dataUrl, filename) {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

/**
 * Carrega uma imagem de uma URL e a retorna como um elemento HTMLImageElement.
 * @param {string} src - A URL da imagem.
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous'; // Necessário para carregar imagens de outro domínio no canvas
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    
    // Garante que a URL sempre use https
    if (src.startsWith('http://' )) {
      img.src = src.replace('http://', 'https://' );
    } else {
      img.src = src;
    }

  });
}

/**
 * Gera uma imagem de evidência combinando detalhes da encomenda, etiqueta e assinatura.
 * @param {object} encomenda - O objeto da encomenda.
 * @param {string} signatureImage - A assinatura em formato Data URL.
 * @returns {Promise<string>} - A imagem de evidência final em formato Data URL (JPEG).
 */
export async function gerarImagemEvidencia(encomenda, signatureImage) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // --- Configurações do Canvas ---
  const canvasWidth = 800;
  const padding = 40;
  const lineHeight = 30;
  let currentY = padding;



  // --- Carregar Imagens ---
  // Renomeado para 'assinaturaImgOriginal' para clareza
  const [etiquetaImg, assinaturaImgOriginal] = await Promise.all([
    loadImage(encomenda.imageUrl),
    loadImage(signatureImage),
  ]);

  // --- Rotacionar a Imagem da Assinatura ---
  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');
  
  // 1. CORREÇÃO: Defina as dimensões ANTES de usar o contexto
  tempCanvas.width = assinaturaImgOriginal.height;
  tempCanvas.height = assinaturaImgOriginal.width;

  tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
  tempCtx.rotate(90 * Math.PI / 180);
  
  // 2. CORREÇÃO: Use a variável correta 'assinaturaImgOriginal'
  tempCtx.drawImage(assinaturaImgOriginal, -assinaturaImgOriginal.width / 2, -assinaturaImgOriginal.height / 2);
  
  const assinaturaImgRotacionada = tempCanvas;
  // --- Fim da Rotação ---

  // --- Calcular Altura do Canvas ---
  const etiquetaAspectRatio = etiquetaImg.height / etiquetaImg.width;
  const etiquetaHeight = (canvasWidth - padding * 2) * etiquetaAspectRatio;
  
  const assinaturaDrawHeight = 200;
  const assinaturaAspectRatio = assinaturaImgRotacionada.width / assinaturaImgRotacionada.height;
  const assinaturaDrawWidth = assinaturaDrawHeight * assinaturaAspectRatio;
  
  const canvasHeight = padding + lineHeight * 5 + etiquetaHeight + padding + assinaturaDrawHeight + padding * 2;

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  // --- Desenhar no Canvas ---
  // 1. Fundo Branco
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // 2. Título
  ctx.fillStyle = 'black';
  ctx.font = 'bold 28px sans-serif';
  ctx.fillText('Comprovante de Retirada de Encomenda', padding, currentY);
  currentY += lineHeight * 2; // Incrementa Y para a próxima seção

  // 3. CORREÇÃO: Detalhes do Texto vêm ANTES da imagem da etiqueta
  ctx.font = '20px sans-serif';
  ctx.fillText(`Encomenda ID: ${encomenda.encomendaId.substring(0, 12)}...`, padding, currentY);
  currentY += lineHeight;
  ctx.fillText(`Unidade: ${encomenda.unidade}`, padding, currentY);
  currentY += lineHeight;
  ctx.fillText(`Destinatário: ${encomenda.destinatario || 'Não informado'}`, padding, currentY);
  currentY += lineHeight;
  ctx.fillText(`Data da Retirada: ${format(new Date(), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}`, padding, currentY);
  currentY += lineHeight * 1.5;

  // 4. Imagem da Etiqueta
  ctx.drawImage(etiquetaImg, padding, currentY, canvasWidth - padding * 2, etiquetaHeight);
  currentY += etiquetaHeight + padding; // Incrementa Y para depois da etiqueta

  // 5. Linha e Título da Assinatura
  ctx.fillText('Assinatura do Recebedor:', padding, currentY);
  currentY += lineHeight;
  
  const assinaturaX = (canvasWidth - assinaturaDrawWidth) / 2;
  ctx.drawImage(assinaturaImgRotacionada, assinaturaX, currentY, assinaturaDrawWidth, assinaturaDrawHeight);

  // 6. Retornar como Data URL
  return canvas.toDataURL('image/jpeg', 0.9);
}